import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Copy, X } from "lucide-react";
import { toast } from "sonner";

export default function TeamSettings() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [submitting, setSubmitting] = useState(false);

  const canInvite = user?.role === "owner";

  const refresh = async () => {
    try {
      const [m, i] = await Promise.all([api.get("/team"), canInvite ? api.get("/invitations") : Promise.resolve({ data: [] })]);
      setMembers(m.data); setInvites(i.data);
    } catch {}
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/invitations", { email, role });
      setEmail("");
      toast.success("Invite created");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/invitations/${id}`); toast.success("Invite revoked"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  return (
    <div className="space-y-10" data-testid="team-settings">
      <div>
        <h2 className="text-xl font-medium text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>Team</h2>
        <p className="text-sm text-zinc-500 mt-1">Invite teammates and manage roles.</p>
      </div>

      {canInvite && (
        <form onSubmit={invite} className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-4">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">Invite a teammate</p>
          <div className="flex flex-wrap gap-3">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@company.com" className="flex-1 min-w-[220px] bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 focus:border-orange-600" data-testid="team-invite-email-input" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-200" data-testid="team-invite-role-select">
              <option value="admin">Admin</option>
              <option value="member">Member</option>
            </select>
            <button type="submit" disabled={submitting} className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="team-invite-submit-btn">
              {submitting ? "Sending…" : "Create invite"}
            </button>
          </div>
          <p className="text-xs text-zinc-500">Email sending is coming in a future phase. For now, copy and share the invite link manually.</p>
        </form>
      )}

      <section>
        <h3 className="text-sm font-medium text-zinc-100 mb-3">Members</h3>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-800">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Auth</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id} className="border-b border-zinc-800/60" data-testid={`team-member-row-${m.user_id}`}>
                  <td className="px-5 py-3 text-zinc-100">{m.name}</td>
                  <td className="px-5 py-3 text-zinc-400 truncate max-w-[260px]">{m.email}</td>
                  <td className="px-5 py-3 text-zinc-300 capitalize">{m.role}</td>
                  <td className="px-5 py-3 text-zinc-500 text-xs capitalize">{m.auth_provider}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {canInvite && invites.length > 0 && (
        <section>
          <h3 className="text-sm font-medium text-zinc-100 mb-3">Pending invites</h3>
          <div className="space-y-2">
            {invites.filter((i) => i.status === "pending").map((i) => (
              <div key={i.invitation_id} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid={`team-invite-row-${i.invitation_id}`}>
                <div className="flex-1">
                  <p className="text-sm text-zinc-100">{i.email}</p>
                  <p className="text-xs text-zinc-500 capitalize">{i.role} · expires {new Date(i.expires_at).toLocaleDateString()}</p>
                </div>
                <button onClick={() => copyInviteLink(i.token)} className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 rounded-md" data-testid={`team-invite-copy-${i.invitation_id}`}>
                  <Copy className="h-3 w-3" /> Copy link
                </button>
                <button onClick={() => revoke(i.invitation_id)} className="p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-zinc-800" data-testid={`team-invite-revoke-${i.invitation_id}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
